import React from 'react';
import moment from 'moment';

import { PropTypes } from 'prop-types';

import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';

import {
   RemoveBottomMargin,
   TwoSideFlexContainer,
   Description,
   SimpleFlexRow,
   SimpleFlexItem,
   DescriptionSummary,
} from '../../../../styledComponents/styledComponents';
import { SIMPLE_DATE_FORMAT, SIMPLE_TIME_FORMAT } from '../../../../constants/general';
import { formatTimeNumberInt } from '../../../../util/NumberFormatter';

import storePropTypes from '../../../../redux/store/propTypes';
import BlockingIndicator from '../../common/BlockingIndicator';
import { CsvExportFromApiButton } from '../../../common';
import { useState, useMemo, useEffect } from 'react';
import JwtRowModal from './JwtRowModal';

import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';

import * as browserStorage from '../../../../misc/BrowserStorageManager';
import JwtMetricsHelp from './JwtTableDescription';
import useRevokedTokenFetcher from '../../../../hooks/useRevokedTokenFetcher';

const { SearchBar } = Search;

const getColumns = (concurrencyIsDesired) => {
   let { isAdmin } = browserStorage.getUserInfo();
   const columns = [
      {
         text: 'JTI',
         dataField: 'tokenPayload.jti',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         classes: 'ip-cell',
      },
      ...(isAdmin
         ? [
              {
                 text: 'Orga',
                 dataField: 'orgaHash',
                 sort: true,
                 style: { verticalAlign: 'middle', textAlign: 'right' },
                 headerAlign: 'right',
                 searchable: false,
                 headerStyle: {
                    whiteSpace: 'normal',
                    maxWidth: '70px',
                 },
              },
           ]
         : []),
      {
         text: 'User',
         dataField: 'tokenPayload.user',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (value) => (value ? value : 'N/A'),
      },
      {
         text: 'Duration/Sum',
         dataField: 'durationSum',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: formatTimeNumberInt,
      },
      {
         text: 'Duration/Avg',
         dataField: 'durationAvg',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: formatTimeNumberInt,
      },
      {
         text: 'Sessions',
         dataField: 'sessionCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right', maxWidth: '60px' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'Streams',
         dataField: 'streamCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'IPs',
         dataField: 'ipCount',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         searchable: false,
         headerAlign: 'right',
         headerStyle: {
            whiteSpace: 'normal',
            maxWidth: '70px',
         },
      },
      {
         text: 'Top Providers',
         dataField: 'topProviders',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Countries',
         dataField: 'topCountries',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Referrers',
         dataField: 'topReferrers',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         formatter: (values) => (values.length === 0 ? 'N/A' : values.join(', ')),
      },
      {
         text: 'Top Agents',
         dataField: 'topAgents',
         hidden: true,
      },
      {
         text: 'First stream start',
         dataField: 'streamStart',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
      },
      {
         text: 'Last stream end',
         dataField: 'streamEnd',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
      },
      {
         text: 'Max Concurrency',
         dataField: 'concurrency',
         sort: true,
         style: { verticalAlign: 'middle', textAlign: 'right' },
         headerAlign: 'right',
         searchable: false,
         hidden: concurrencyIsDesired ? false : true,
      },
   ];

   columns.push({
      text: 'revoked',
      dataField: 'revoked',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'center' },
      formatter: (cell) => (cell === 'N/A' ? 'N/A' : <BlockingIndicator isBlocked={cell === true} />),
      headerAlign: 'center',
      headerStyle: { width: '80px' },
      searchable: false,
   });

   columns.push({
      text: '',
      dataField: '__details',
      isDummyField: true,
      searchable: false,
      headerStyle: {
         width: '36px',
         minWidth: '36px',
      },
      style: {
         textAlign: 'center',
         verticalAlign: 'middle',
         padding: '0',
      },
      formatter: () => (
         <>
            <div className='row-action' data-tooltip-id={'openDetails'} data-tooltip-html={'Get more details'}>
               <span className='row-action-icon'>
                  <i id='detailMarker' className='bi bi-chevron-right text-blue'></i>
               </span>
            </div>
            <ReactTooltip
               id='openDetails'
               place={'top'}
               className={'reduced-line-height bring-on-top'}
               backgroundColor={'rgba(0,0,0,0.9)'}
               textColor={'rgb(240,240,240)'}
               style={{ textAlign: 'center' }}
            />
         </>
      ),
   });

   return columns;
};

function JwtTable({ data, concurrencyIsDesired, url, httpHeader, cancelToken, requestBody }) {
   const [modal, setModal] = useState({
      toggleShow: false,
      rowData: {},
   });

   const rowEvents = {
      onClick: (e, rowData) => {
         setModal({ toggleShow: true, rowData });
      },
      onKeyDown: (e, rowData) => {
         if (e.key === 'Enter') {
            setModal({ toggleShow: true, rowData });
         }
      },
   };

   const { toggleShow, rowData } = modal;

   const { fetchRevokedTokens, revokedMap } = useRevokedTokenFetcher();

   const { isAdmin } = browserStorage.getUserInfo();
   const jtis = useMemo(
      () =>
         data.map((row) =>
            isAdmin
               ? { jti: row.tokenPayload.jti, orga: row.orgaHash }
               : { jti: row.tokenPayload.jti },
         ),
      [data, isAdmin],
   );

   useEffect(() => {
      fetchRevokedTokens(jtis);
   }, [fetchRevokedTokens, jtis]);

   const tableData = useMemo(() => {
      if (!revokedMap) return data.map((row) => ({ ...row, revoked: 'N/A' }));
      return data.map((row) => ({ ...row, revoked: revokedMap[row.tokenPayload.jti] === true }));
   }, [data, revokedMap]);

   const columns = useMemo(() => getColumns(concurrencyIsDesired), [concurrencyIsDesired]);

   return (
      <>
         {data.length > 0 && (
            <>
               <details style={{ marginBottom: '10px' }}>
                  <DescriptionSummary>Table columns descriptions</DescriptionSummary>
                  <JwtMetricsHelp />
               </details>
               <RemoveBottomMargin>
                  <ToolkitProvider keyField='tokenPayload.jti' data={tableData} columns={columns} bootstrap4 search>
                     {(props) => (
                        <>
                           <TwoSideFlexContainer $marginTop={'20px'}>
                              <Description alpha={0.5} fontSizePX={14} marginTopPx={6} marginBottomPx={15}>
                                 Click on a table row to open a stream break down for the respective Playback Token
                              </Description>
                              <SimpleFlexRow>
                                 <SearchBar {...props.searchProps} tableId={'token'} />
                                 <SimpleFlexItem $marginLeftInPx={8} $bottomMarginPx={10}>
                                    <CsvExportFromApiButton
                                       url={url}
                                       header={httpHeader}
                                       cancelToken={cancelToken}
                                       filename={'Playback_Token_Details'}
                                       inputStyle
                                       heightInPx={34}
                                       method={'post'}
                                       body={requestBody}
                                    />
                                 </SimpleFlexItem>
                              </SimpleFlexRow>
                           </TwoSideFlexContainer>
                           <BootstrapTable
                              {...props.baseProps}
                              id='misuseTable'
                              pagination={paginationFactory({
                                 sizePerPage: 10,
                                 hideSizePerPage: true,
                              })}
                              striped
                              hover
                              // expandRow={expandRow}
                              rowEvents={rowEvents}
                              condensed={true}
                              classes='table-responsive-xl'
                           />
                        </>
                     )}
                  </ToolkitProvider>
               </RemoveBottomMargin>

               {toggleShow && (
                  <JwtRowModal
                     rowData={rowData}
                     closeModal={() =>
                        setModal({
                           ...modal,
                           toggleShow: false,
                        })
                     }
                     onRevokeSuccess={() => fetchRevokedTokens(jtis)}
                     isRevoked={revokedMap !== undefined ? revokedMap[rowData.tokenPayload?.jti] === true : null}
                  />
               )}
            </>
         )}
      </>
   );
}

JwtTable.propTypes = {
   data: PropTypes.arrayOf(storePropTypes.jwtMisuseResultRow),
   concurrencyIsDesired: PropTypes.bool,
   url: PropTypes.string,
   httpHeader: PropTypes.object,
   cancelToken: PropTypes.object,
   requestBody: PropTypes.object,
};

export default JwtTable;
